import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Checkbox,
  Flex,
  Modal,
  Paper,
  ScrollArea,
  Space,
  Text,
  Title
} from '@mantine/core';
import { IconAlertCircle, IconBrandGoogleDrive, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { GoogleDriveAccountConflictErrorModal } from 'components/GoogleDriveAccountConflictErrorModal';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { GoogleDriveIcon } from 'icons/GoogleDriveIcon';
import { useTranslation } from 'react-i18next';
import { GoogleDriveService } from 'services/google-drive-service';
import { NotificationService } from 'services/notification-service';
import { GOOGLE_DRIVE_ACCOUNTS_QUERY_KEY } from 'shared/constants/query-keys';
import {
  DeleteGoogleDriveAccountConflictDto,
  GoogleDriveAccount,
  GoogleDriveAuthUrl
} from 'shared/interfaces/GoogleDrive';
import { Resource } from 'shared/interfaces/Resource';
import { openPopup } from 'shared/utils/navigation';

interface GoogleDriveAccountHandlerProps extends BoxProps {
  onAccounClick?: (account: GoogleDriveAccount) => void;
}

export function GoogleDriveAccountHandler({ onAccounClick, ...boxProps }: GoogleDriveAccountHandlerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();
  const [conflictingResources, setConflictingResources] = useState<Resource[]>([]);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [googleDriveConsentNoticeAccepted, setGoogleDriveConsentNoticeAccepted] = useState(false);
  const [googleDriveConsentModalOpen, setGoogleDriveConsentModalOpen] = useState(false);

  const openConflictModal = () => setConflictModalOpen(true);
  const closeConflictModal = () => setConflictModalOpen(false);

  useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const { authCode } = event.data;

      if (authCode) {
        registerGoogleDriveAccountMutation.error || registerGoogleDriveAccountMutation.isLoading
          ? null
          : registerGoogleDriveAccountMutation.mutate(authCode);
      }
    };

    window.addEventListener('message', receiveMessage, false);

    // Return a cleanup function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('message', receiveMessage, false);
    };
  }, []);

  const googleDriveAccountsQuery = useQuery({
    queryKey: [GOOGLE_DRIVE_ACCOUNTS_QUERY_KEY],
    queryFn: () => GoogleDriveService.getAccounts(),
    enabled: !!subscription.product.enableGoogleDrive
  });

  const registerGoogleDriveAccountMutation = useMutation({
    mutationFn: GoogleDriveService.registerAccount,
    onSuccess: (googleDriveAccount: GoogleDriveAccount) => {
      queryClient.setQueryData([GOOGLE_DRIVE_ACCOUNTS_QUERY_KEY], (existingAccounts: GoogleDriveAccount[] = []) => [
        ...existingAccounts,
        googleDriveAccount
      ]);
      NotificationService.showResult(true, 'FEED_BACK.GOOGLE_DRIVE.SUCCESS.ADD_ACCOUNT');
    },
    onError: (error: AxiosError) => {
      error.response?.status === HttpStatusCode.Conflict
        ? NotificationService.showResult(false, 'FEED_BACK.GOOGLE_DRIVE.FAILURE.CONFLICT')
        : NotificationService.showResult(false, 'FEED_BACK.GOOGLE_DRIVE.FAILURE.ADD_ACCOUNT');
    }
  });

  const openGoogleDriveAuthPopup = useMutation({
    mutationFn: GoogleDriveService.getAuthUrl,
    onSuccess: (googleDriveAuthUrl: GoogleDriveAuthUrl) => openPopup(googleDriveAuthUrl.url),
    onError: () => NotificationService.showResult(false, 'FEED_BACK.GOOGLE_DRIVE.FAILURE.ADD_ACCOUNT')
  });

  const deleteGoogleDriveAccountMutation = useMutation({
    mutationFn: GoogleDriveService.deleteAccount,
    onSuccess: (googleDriveAccount: GoogleDriveAccount) => {
      queryClient.setQueryData([GOOGLE_DRIVE_ACCOUNTS_QUERY_KEY], (existingAccounts: GoogleDriveAccount[] = []) =>
        existingAccounts.filter((account) => account._id !== googleDriveAccount._id)
      );
      NotificationService.showResult(true, 'FEED_BACK.GOOGLE_DRIVE.SUCCESS.DELETE_ACCOUNT');
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.Conflict) {
        const deleteGoogleDriveAccountConflictDto = error.response?.data as DeleteGoogleDriveAccountConflictDto;
        const conflictingResources = deleteGoogleDriveAccountConflictDto.resources;

        if (conflictingResources && conflictingResources.length > 0) {
          setConflictingResources(conflictingResources);
          openConflictModal();

          NotificationService.showResult(false, 'FEED_BACK.GOOGLE_DRIVE.FAILURE.RESOURCE_CONFLICT');
        }
      } else {
        NotificationService.showResult(false, 'FEED_BACK.GOOGLE_DRIVE.FAILURE.DELETE_ACCOUNT');
      }
    }
  });

  if (googleDriveAccountsQuery.isError) return <ErrorAlert h={boxProps.h} />;
  if (googleDriveAccountsQuery.isInitialLoading) return <FullScreenLoader h={boxProps.h} />;
  const googleDriveAccounts = googleDriveAccountsQuery.data || [];

  const CreateButton = ({ ...buttonProps }: ButtonProps) => (
    <Button
      variant="default"
      leftIcon={<GoogleDriveIcon w={18} h={18} />}
      onClick={() => setGoogleDriveConsentModalOpen(true)}
      {...buttonProps}>
      {t('GOOGLE_DRIVE.CONNECT_GOOGLE_DRIVE_ACCOUNT')}
    </Button>
  );
  return (
    <>
      <UpgradePlanBlurCard
        message={t('GOOGLE_DRIVE.UPGRADE.DESCRIPTION')}
        showCard={!subscription.product.enableGoogleDrive}>
        <Box {...boxProps}>
          <Flex justify="space-between" align="center">
            <Flex direction="row" align="center">
              <Title order={5}>{t('GOOGLE_DRIVE.CONNECTED_DRIVES')}</Title>
              <Space w={10} />
              <Badge px={4} mt={3} size="xs" color="gray">
                {googleDriveAccounts.length}
              </Badge>
            </Flex>
            {googleDriveAccounts.length > 0 && <CreateButton variant="default" />}
          </Flex>
          <ScrollArea w="100%" h="100%" mt={10}>
            {googleDriveAccounts.map((account, idx) => (
              <Paper
                key={idx}
                className={onAccounClick ? 'g-hover-style' : ''}
                p={5}
                mt={idx === 0 ? 0 : 5}
                mb={idx === googleDriveAccounts.length - 1 ? 0 : 5}
                px={10}
                withBorder
                onClick={() => onAccounClick && onAccounClick(account)}>
                <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
                  <Flex direction="row" align="center">
                    <Text>{account.userInfo.email}</Text>
                  </Flex>

                  <ActionIcon
                    color="red"
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteGoogleDriveAccountMutation.mutate(account._id);
                    }}>
                    <IconTrash size={22} />
                  </ActionIcon>
                </Flex>
              </Paper>
            ))}
            {googleDriveAccounts.length == 0 && subscription.product.enableGoogleDrive && (
              <NoDataFound
                h="100%"
                size="xs"
                icon={<IconBrandGoogleDrive />}
                title={t('NO_DATA_FOUND.GOOGLE_DRIVE.TITLE')}
                subTitle={t('NO_DATA_FOUND.GOOGLE_DRIVE.SUB_TITLE')}
                button={<CreateButton size="xs" />}
              />
            )}
          </ScrollArea>
        </Box>
      </UpgradePlanBlurCard>
      <GoogleDriveAccountConflictErrorModal
        conflictedResources={conflictingResources}
        errorModalOpened={conflictModalOpen}
        onClose={() => {
          setConflictingResources([]);
          closeConflictModal();
        }}
      />
      <Modal
        opened={googleDriveConsentModalOpen}
        onClose={() => setGoogleDriveConsentModalOpen(false)}
        size={600}
        title={<Title order={4}>{t('CONSENTS.GOOGLE_DRIVE.MODAL_TITLE')}</Title>}>
        <Alert icon={<IconAlertCircle size={16} />} title="" color="lime">
          <ScrollArea h={500}>
            <Title order={6}>{t('CONSENTS.GOOGLE_DRIVE.TITLE')}</Title>
            <Space h={10} />

            <Text>
              <Text fw={700}>{t('CONSENTS.GOOGLE_DRIVE.SECTION_1.HEADER')}</Text>
              {t('CONSENTS.GOOGLE_DRIVE.SECTION_1.CONTENT')}
              <Space h={10} />
              {t('CONSENTS.GOOGLE_DRIVE.SECTION_1.PRIVACY_LINKS')}
            </Text>
            <Space h={10} />

            <Text>
              <Text fw={700}>{t('CONSENTS.GOOGLE_DRIVE.SECTION_2.HEADER')}</Text>
              {t('CONSENTS.GOOGLE_DRIVE.SECTION_2.CONTENT')}
            </Text>
            <Space h={10} />

            <Text>
              <Text fw={700}>{t('CONSENTS.GOOGLE_DRIVE.SECTION_3.HEADER')}</Text>
              {t('CONSENTS.GOOGLE_DRIVE.SECTION_3.CONTENT')}
            </Text>
            <Space h={10} />

            <Text>
              <Text fw={700}>{t('CONSENTS.GOOGLE_DRIVE.SECTION_4.HEADER')}</Text>
              {t('CONSENTS.GOOGLE_DRIVE.SECTION_4.CONTENT')}
              <Space h={10} />
              {t('CONSENTS.GOOGLE_DRIVE.SECTION_4.ACCOUNT_DELETION')}
            </Text>
          </ScrollArea>
        </Alert>
        <Space h={30} />
        <Flex direction="row" align="center" justify="space-evenly" gap="md">
          <Checkbox
            checked={googleDriveConsentNoticeAccepted}
            onChange={(event) => setGoogleDriveConsentNoticeAccepted(event.currentTarget.checked)}
            label={t('CONSENTS.GOOGLE_DRIVE.CHECKBOX_LABEL')}></Checkbox>
          <Button
            variant="outline"
            size="sm"
            compact
            disabled={!googleDriveConsentNoticeAccepted}
            loading={openGoogleDriveAuthPopup.isLoading}
            onClick={() => {
              setGoogleDriveConsentModalOpen(false);
              openGoogleDriveAuthPopup.mutate();
            }}>
            {t('APP.ACTIONS.CONFIRM')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
